import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import {
  createCompanyOption,
  resolveCompanyLabel,
  searchCompanies,
} from '@/features/crm/companies/select';
import {
  createContactOption,
  resolveContactLabel,
  searchContacts,
} from '@/features/crm/contacts/select';
import { CompanyProfileFields } from '@/features/crm/companies/components/CompanyProfileFields';
import { useCompany } from '@/features/crm/companies/queries';
import { useIcpSegments } from '@/features/crm/icpSegments';
import { MemberSelect } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { ICP_FITS, LEAD_SOURCES, LEAD_STATUSES } from '../constants';
import type { LeadInput } from '../types';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  description: z.string(),
  source: z.enum(['upwork', 'linkedin', 'website', 'referral', 'outbound', 'fiverr', 'rfp', 'other']),
  status: z.enum(['new', 'qualified', 'disqualified', 'converted']),
  company: z.string(),
  contact: z.string(),
  owner: z.string(),
  pain: z.string(),
  icp_fit: z.enum(['', 'fits', 'partial', 'no_fit']),
  icp_segment: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'title',
  'description',
  'source',
  'status',
  'company',
  'contact',
  'owner',
  'pain',
  'icp_fit',
  'icp_segment',
] as const;

// Radix Select can't carry an empty value — this stands in for "not set".
const NONE = '__none__';

interface LeadFormProps {
  defaultValues?: Partial<FormValues>;
  /** Name of the lead's current segment, so an archived one still shows. */
  currentSegmentName?: string | null;
  submitLabel: string;
  onSubmit: (values: LeadInput) => Promise<unknown>;
  onCancel: () => void;
}

export function LeadForm({
  defaultValues,
  currentSegmentName,
  submitLabel,
  onSubmit,
  onCancel,
}: LeadFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      source: 'website',
      status: 'new',
      company: '',
      contact: '',
      owner: '',
      pain: '',
      icp_fit: '',
      icp_segment: '',
      ...defaultValues,
    },
  });
  const companyId = useWatch({ control: form.control, name: 'company' });
  const companyQuery = useCompany(companyId);
  const segmentsQuery = useIcpSegments();
  const segmentOptions = (segmentsQuery.data?.results ?? []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));
  const currentSegment = defaultValues?.icp_segment;
  // Only once the live list is in — before that a live segment would look archived.
  if (
    segmentsQuery.isSuccess &&
    currentSegment &&
    !segmentOptions.some((o) => o.value === currentSegment)
  ) {
    // Archived segments stay on the leads that have them (ADR-108).
    segmentOptions.push({
      value: currentSegment,
      label: `${currentSegmentName ?? 'Segment'} (archived)`,
    });
  }

  async function handleSubmit(values: FormValues) {
    const input: LeadInput = {
      title: values.title,
      description: values.description,
      source: values.source,
      status: values.status,
      company: values.company ? Number(values.company) : null,
      contact: values.contact ? Number(values.contact) : null,
      owner: values.owner ? Number(values.owner) : null,
      pain: values.pain,
      icp_fit: values.icp_fit,
      icp_segment: values.icp_segment ? Number(values.icp_segment) : null,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-5">
        <FormSection variant="card" title="Lead" description="What's this lead about?">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Inbound from Upwork — MVP build" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea rows={4} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          variant="card"
          title="Pipeline"
          description="Where it came from and where it stands."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEAD_SOURCES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Qualification"
          description="Why they're talking to us, and how they fit your ICPs."
        >
          <FormField
            control={form.control}
            name="pain"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pain</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder="The problem they need solved, in their words"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="icp_fit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ICP fit</FormLabel>
                  <Select
                    value={field.value || NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? '' : v)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>Not assessed</SelectItem>
                      {ICP_FITS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="icp_segment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    ICP segment
                    <InfoHint text="Which of your ideal customer profiles this lead matches. Managed under CRM › ICP Segments." />
                  </FormLabel>
                  <Select
                    value={field.value || NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? '' : v)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>No segment</SelectItem>
                      {segmentOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Relations"
          description="Who and what is this attached to?"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="company-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchCompanies}
                      resolveLabel={resolveCompanyLabel}
                      onCreate={createCompanyOption}
                      placeholder="No company"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="contact-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchContacts}
                      resolveLabel={resolveContactLabel}
                      onCreate={createContactOption}
                      placeholder="No contact"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {companyId && companyQuery.data ? (
            <div
              className="bg-muted/40 grid gap-3 rounded-md border p-3 sm:grid-cols-2"
              aria-label="Company profile"
            >
              <CompanyProfileFields profile={companyQuery.data} />
              <p className="text-muted-foreground text-xs sm:col-span-2">
                Set on the company — edit it there.
              </p>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="owner"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Owner
                  <InfoHint text="The sales person responsible for this lead." />
                </FormLabel>
                <FormControl>
                  <MemberSelect
                    queryKey="lead-owner-picker"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Assign an owner"
                    roles={CRM_OWNER_ROLES}
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
